import React, { useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { ContentType, IContent, IContentAssociatedFlight, IUser } from "@vmosolutions/oc-interfaces";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LogoIcon from "src/themes/assets/logo_icon.svg?react";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { ContentThumbnail } from "./ContentThumbnail";
import { DateDisplay } from "src/components/DateDisplay";
import FlightIcon from "@mui/icons-material/Flight";
import { ClickAwayListener, IconButton, Grid, useTheme, Theme, Popover, Button } from "@mui/material";
import { FlightChip } from "src/components/Flights/FlightChip";
import { ShowUserInfo } from "src/components/ShowUserInfo/ShowUserInfo";
import { getInitialValues as getContentWithFlights } from "src/pages/ContentManagement/ManageContentWindow";
import { LoadingMessage } from "../LoadingMessage/LoadingMessage";
// Permissions imports
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector } from "react-redux";
import { hasPermission, PolicyAction, PolicyResource } from "@vmosolutions/oc-interfaces";

interface IContentTableCellProps {
  data: IContent;
  cellId: string;
}

const AssociatedFlights = (theme: Theme): any => ({
  flexWrap: "wrap",
  justifyContent: "flex-start",
  "& .flights": {
    backgroundColor: "forestgreen", //theme.palette.success.dark,
  },
  "& .button": {
    border: `1px solid ${theme.palette.background.default}`,
    color: theme.palette.primary.contrastText,
    display: "flex",
    alignItems: "flex-end",
    minWidth: "unset",
    borderRadius: "3px",
    boxShadow: theme.shadows[2],
    margin: "2px",
    padding: "1px 2px",
    fontSize: "10px",
    fontWeight: "700px",
    lineHeight: "10px",
    "&.active": {
      border: `1px solid ${theme.palette.primary.contrastText}`,
    },
    "& svg": {
      width: "16px",
      height: "16px",
    },
  },
});

export const ContentTableCell = (props: IContentTableCellProps): JSX.Element => {
  const { data, cellId } = props;
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState<Element>();
  const [contentWithFlights, setContentWithFlights] = useState<IContent>();
  const [loadingFlights, setIsLoadingFlights] = useState<boolean>(false);

  // Permissions
  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);
  const canEditContent = hasPermission(policies, PolicyResource.Content, PolicyAction.List);
  const canUpdateFlights = hasPermission(policies, PolicyResource.Flight, PolicyAction.Update);

  const openManageContentWindow = useCallback((): void => {
    dispatch(
      openWindow(
        "Edit Content",
        "ManageContentWindow",
        [],
        {
          contentId: data?._id,
        },
        "context"
      )
    );
  }, [dispatch, data]);

  const openReviewContentWindow = useCallback((): void => {
    dispatch(openWindow("Review Content", "ReviewContentWindow", [], { content: data }, "content"));
  }, [dispatch, data]);

  const displayEvents = useCallback(
    (type: "flights" | "posflights" | "nonflights", content: IContent) =>
      (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        console.log(type);
        event.stopPropagation(); // Ensured that click doesn't select/deselect table rows.
        event.persist();

        if (content?.associatedFlights?.length === 0) {
          setContentWithFlights(undefined);
          return;
        }

        const hasFullFlights = content?.associatedFlights[0].hasOwnProperty("_id");
        if (hasFullFlights && contentWithFlights?._id !== content?._id) {
          setContentWithFlights(content);
        } else if (!hasFullFlights && contentWithFlights?._id !== content?._id) {
          const getContentFlights = async (): Promise<void> => {
            setIsLoadingFlights(true);
            const response = await getContentWithFlights(content._id);
            const contentWithFlights = response?._id ? response : null;
            setContentWithFlights(contentWithFlights);
            setIsLoadingFlights(false);
          };
          getContentFlights();
        }

        setAnchorEl(event.currentTarget);
      },
    [contentWithFlights?._id]
  );

  const sourceIcon = useMemo(() => {
    switch (data?.source?.iconName) {
      case "OC":
        return <LogoIcon style={{ width: 34 }} />;
      default:
        return null;
    }
  }, [data]);

  // Return more complex validation info is type of logseet
  const reviewStatus = useMemo(() => {
    if (data?.contentType?.type === ContentType.JourneyLogSheet) {
      const reviewed = data?.validationHistory?.length > 0;
      const reviewStatus =
        reviewed && data?.isValid ? "Valid" : reviewed && !data?.isValid ? "Contains Errors" : "Not Reviewed";
      const color = reviewStatus === "Valid" ? "lightgreen" : reviewStatus === "Contains Errors" ? "red" : "inherit";
      return (
        <Grid container direction="column">
          <Grid item style={{ margin: "2px 8px", color }}>
            {reviewStatus}
          </Grid>
          <Button
            disabled={!canUpdateFlights || !canEditContent}
            style={{ width: 60, padding: "0px 8px", fontSize: 12 }}
            onClick={() => openReviewContentWindow()}
          >
            Review
          </Button>
        </Grid>
      );
    }
    return <Grid item>{data?.isValid}</Grid>;
  }, [data, canUpdateFlights, canEditContent, openReviewContentWindow]);
  switch (cellId) {
    case "manageContent":
      return (
        <IconButton
          style={{ width: 40, height: 40 }}
          id="popout_summary_btn"
          disabled={!canEditContent}
          onClick={openManageContentWindow}
          size="large"
        >
          <OpenInNewIcon />
        </IconButton>
      );
    case "thumbnail":
      return <ContentThumbnail size={64} contentId={data._id} fileType={data.fileType} fileName={data.fileName} />;
    case "contentType":
      return <p>{data[cellId]?.title}</p>;
    case "fileName":
    case "fileType":
      return <p>{data[cellId]}</p>;
    case "createdAt":
    case "modifiedAt": {
      const date = data[cellId];
      return <DateDisplay date={date} />;
    }
    case "uploadedBy":
      return (
        <ShowUserInfo userId={data[cellId]} getUserLabel={(user: IUser) => `${user?.firstName} ${user?.lastName}`} />
      );

    case "associatedFlights":
      //TODO: LOGSHEET i think this commented out code is not wanted
      // const count = data?.associatedFlights?.length;
      // return !data?.associatedFlights ||
      //   data?.associatedFlights?.length === 0 ||
      //   data?.associatedFlights[0] === null ? (
      //   <p>None</p>
      // ) : (
      //   <p>{`${count} Flight${count > 1 && "s" ? "s" : ""}`}</p>
      // );
      // return <p>{data?.associatedFlights?.length}</p>;
      return data?.associatedFlights?.length > 0 ? (
        <ClickAwayListener onClickAway={() => setAnchorEl(undefined)}>
          <Grid container>
            <Grid item sx={(theme) => AssociatedFlights(theme)}>
              <Button className={"flights button active"} onClick={displayEvents("flights", data)}>
                <FlightIcon />
                <div>{`x${data.associatedFlights?.length}`}</div>
                {loadingFlights && <LoadingMessage />}
              </Button>
            </Grid>
            <Popover
              anchorEl={anchorEl}
              anchorReference="anchorEl"
              anchorOrigin={{ vertical: "center", horizontal: "left" }}
              transformOrigin={{ vertical: "center", horizontal: "right" }}
              open={!!anchorEl && !!contentWithFlights}
              onClose={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                event.stopPropagation();
                setAnchorEl(undefined);
              }}
            >
              {!!contentWithFlights &&
                (contentWithFlights?.associatedFlights as IContentAssociatedFlight[]).map(
                  (data: IContentAssociatedFlight, index: number) => <FlightChip data={data} key={`flight-${index}`} />
                )}
            </Popover>
          </Grid>
        </ClickAwayListener>
      ) : null;
    case "source":
      return (
        <Grid container direction="column">
          <Grid item>{sourceIcon}</Grid>
          <p style={{ margin: 0, padding: 0 }}>{data?.source?.name}</p>
        </Grid>
      );
    case "isValid":
      return reviewStatus;
    default:
      return <p>Not Found</p>;
  }
};
